import {helpdeskQueries} from '@app/dashboard/helpdesk-queries';
import {helpCenterQueries} from '@app/help-center/help-center-queries';
import {
  articlePageLoader,
  categoryPageLoader,
} from '@app/help-center/help-center-routes';
import {authGuard} from '@common/auth/guards/auth-route';
import {queryClient} from '@common/http/query-client';
import {searchParamsFromUrl} from '@ui/utils/urls/search-params-from-url';
import {RouteObject} from 'react-router';

export const studentRoutes: RouteObject[] = [
  {
    path: 'student',
    lazy: () => import('@app/student/layout/student-layout'),
    /**
     * One portal, three kinds of visitor.
     *
     * A student sees their own file. A staff member who has opened a student
     * sees that student's. A staff member who has opened nobody sees the
     * screens themselves, empty — which is what they came for when they are
     * checking that a page still looks right.
     *
     * No redirect: being bounced somewhere else for asking to look at a page is
     * worse than the page being empty, as long as the page says so. The bar at
     * the top of the layout does, and the server refuses every write until a
     * student is actually chosen.
     */
    loader: () => authGuard({requireLogin: true}),
    children: [
      {
        // Results for the English placement exam and the five career tests.
        // The tests themselves live in the public catalogue; this is where the
        // student's own results come back to them.
        path: 'assessments',
        lazy: () => import('@app/student/assessments/student-assessments-page'),
      },
      {
        index: true,
        lazy: () => import('@app/student/dashboard/student-dashboard-page'),
      },
      // CV Builder
      {
        path: 'cv',
        lazy: () => import('@app/student/cv-builder/cv-list-page'),
      },
      {
        path: 'cv/new',
        lazy: () => import('@app/student/cv-builder/cv-editor-page'),
      },
      {
        path: 'cv/:cvId',
        lazy: () => import('@app/student/cv-builder/cv-editor-page'),
      },
      {
        path: 'cv/:cvId/view',
        lazy: () => import('@app/student/cv-builder/cv-view-page'),
      },
      // Cover Letters
      {
        path: 'cover-letters',
        lazy: () => import('@app/student/cover-letters/cover-letter-list-page'),
      },
      {
        path: 'cover-letters/new',
        lazy: () =>
          import('@app/student/cover-letters/cover-letter-editor-page'),
      },
      {
        path: 'cover-letters/:letterId',
        lazy: () =>
          import('@app/student/cover-letters/cover-letter-editor-page'),
      },
      // SOP / Motivation Letters
      {
        path: 'sop',
        lazy: () => import('@app/student/sop/sop-list-page'),
      },
      {
        path: 'sop/new',
        lazy: () => import('@app/student/sop/sop-editor-page'),
      },
      {
        path: 'sop/:sopId',
        lazy: () => import('@app/student/sop/sop-editor-page'),
      },
      // Plagiarism / AI Detection
      {
        path: 'plagiarism',
        lazy: () => import('@app/student/plagiarism/plagiarism-page'),
      },
      // University Matching
      {
        path: 'university-matching',
        lazy: () =>
          import('@app/student/university-matching/university-matching-page'),
      },
      {
        path: 'university-matching/:matchId',
        lazy: () =>
          import(
            '@app/student/university-matching/university-match-result-page'
          ),
      },
      // Exam Placement
      {
        path: 'exam-placement',
        lazy: () =>
          import('@app/student/exam-placement/exam-placement-page'),
      },
      {
        path: 'exam-placement/:resultId',
        lazy: () =>
          import('@app/student/exam-placement/exam-placement-result-page'),
      },
      // Document Generator
      {
        path: 'documents',
        lazy: () => import('@app/student/documents/document-list-page'),
      },
      {
        path: 'documents/new',
        lazy: () => import('@app/student/documents/document-editor-page'),
      },
      {
        path: 'documents/:documentId',
        lazy: () => import('@app/student/documents/document-editor-page'),
      },
      // Masertak Concierge Assistant (tool-using agent)
      {
        path: 'concierge',
        lazy: () => import('@app/student/concierge/concierge-chat-page'),
      },
      // Career Assistant
      {
        path: 'career',
        lazy: () => import('@app/student/career/career-list-page'),
      },
      {
        path: 'career/new',
        lazy: () => import('@app/student/career/career-chat-page'),
      },
      {
        path: 'career/:sessionId',
        lazy: () => import('@app/student/career/career-chat-page'),
      },
      // Scholarship Assistant
      {
        path: 'scholarship-assistant',
        lazy: () => import('@app/student/scholarship-assistant/scholarship-assistant-list-page'),
      },
      {
        path: 'scholarship-assistant/new',
        lazy: () => import('@app/student/scholarship-assistant/scholarship-assistant-chat-page'),
      },
      {
        path: 'scholarship-assistant/:conversationId',
        lazy: () => import('@app/student/scholarship-assistant/scholarship-assistant-chat-page'),
      },
      // My booked services (assisted applications + à-la-carte services)
      {
        path: 'applications',
        lazy: () => import('@app/student/applications/my-applications-page'),
      },
      {
        path: 'contribute',
        lazy: () => import('@app/student/contribute/suggest-opportunity-page'),
      },
      // CRM-backed services. Read-only: the CRM is where the work happens, Flash
      // is where the student sees it.
      {
        // Every rating the student has left or still owes, both kinds together.
        path: 'ratings',
        lazy: () => import('@app/student/portal/student-ratings-page'),
      },
      {
        // The files on the student's CRM record. Listed here, served by the
        // CRM — see student-files-page for why that split is deliberate.
        path: 'documents/shared',
        lazy: () => import('@app/student/portal/student-files-page'),
      },
      {
        path: 'services',
        lazy: () => import('@app/student/portal/student-services-page'),
      },
      {
        path: 'services/:serviceId',
        lazy: () => import('@app/student/portal/student-service-detail-page'),
      },
      // Finance: invoices, payments and orders in one place. `/student/payments`
      // was already taken by the older my-payments view, which this supersedes.
      {
        path: 'payments',
        lazy: () => import('@app/student/portal/student-finance-page'),
      },
      // The student's own page: profile, application intake, and the account
      // settings that used to live under their own navbar. The tab is in the
      // path so the sidebar can link to one and the back button works.
      {
        path: 'profile',
        lazy: () => import('@app/student/profile/student-profile-page'),
      },
      {
        path: 'profile/:tab',
        lazy: () => import('@app/student/profile/student-profile-page'),
      },
      // The standalone page this used to be, kept as a redirect so existing
      // links and bookmarks land on the tab rather than a 404.
      {
        path: 'application',
        lazy: () => import('@app/student/profile/application-redirect'),
      },
      // Masertak Club — points balance and the statement behind it.
      {
        path: 'club',
        lazy: () => import('@app/student/club/student-club-page'),
      },
      // Every AI tool with its real cost on the card, rather than eight
      // identical tiles that all look free.
      {
        path: 'tools',
        lazy: () => import('@app/student/tools/student-tools-page'),
      },
      // Appointments: the CRM's booked calls, with the joining link while it
      // is still live.
      {
        path: 'appointments',
        lazy: () => import('@app/student/portal/student-appointments-page'),
      },
      // Everything dated, merged into one calendar.
      {
        path: 'important-dates',
        lazy: () => import('@app/student/portal/student-dates-page'),
      },
      // Support: CRM tickets + announcements. Raising and replying still happen
      // in the help centre, so there is one place a conversation lives.
      {
        path: 'support',
        lazy: () => import('@app/student/portal/student-support-page'),
      },
      // The help centre, inside the portal. Same components and the same
      // queries as /hc — they notice they are under /student and drop the
      // public navbar — so there is no second help centre to keep in step,
      // and a student reading an article never leaves their own layout.
      {
        path: 'hc',
        lazy: () => import('@app/help-center/homepage/hc-landing-page'),
        loader: async () =>
          queryClient.ensureQueryData(
            helpCenterQueries.categories.landingPageData(),
          ),
      },
      {
        path: 'hc/articles/:articleId/:articleSlug',
        lazy: () => import('@app/help-center/articles/article-page/article-page'),
        loader: async ({params}) => articlePageLoader(params),
      },
      {
        path: 'hc/articles/:categoryId/:sectionId/:articleId',
        lazy: () => import('@app/help-center/articles/article-page/article-page'),
        loader: async ({params}) => articlePageLoader(params),
      },
      {
        path: 'hc/articles/:categoryId/:sectionId/:articleId/:articleSlug',
        lazy: () => import('@app/help-center/articles/article-page/article-page'),
        loader: async ({params}) => articlePageLoader(params),
      },
      {
        path: 'hc/categories/:categoryId/:slug',
        lazy: () => import('@app/help-center/categories/category-page'),
        loader: async ({params}) => categoryPageLoader(params),
      },
      {
        path: 'hc/categories/:categoryId/:sectionId/:slug',
        lazy: () => import('@app/help-center/categories/category-page'),
        loader: async ({params}) => categoryPageLoader(params),
      },
      {
        path: 'hc/search/:query',
        lazy: () => import('@app/help-center/search/hc-search-page'),
        loader: ({params}) =>
          queryClient.ensureQueryData(
            helpCenterQueries.articles.search({
              query: params.query,
              perPage: '30',
            }),
          ),
      },
      // Help-desk tickets, wearing the portal's chrome. These load the SAME
      // components /hc/tickets does — the pages notice they are under /student
      // and drop the public navbar — so a student never lands on a differently
      // shaped page, and there is no second copy to keep in step.
      {
        path: 'tickets',
        lazy: () =>
          import(
            '@app/help-center/tickets-portal/ticklets-table/tickets-table-page'
          ),
        loader: async ({request}) => {
          const redirect = authGuard();
          if (redirect) return redirect;
          return Promise.all([
            await queryClient.ensureQueryData(
              helpCenterQueries.customerConversations.index(
                searchParamsFromUrl(request.url),
              ),
            ),
            await queryClient.ensureQueryData(
              helpdeskQueries.statuses.dropdownList('user'),
            ),
          ]);
        },
      },
      {
        path: 'tickets/new',
        lazy: () =>
          import(
            '@app/help-center/tickets-portal/new-ticket-page/new-ticket-page'
          ),
        loader: async () => {
          const redirect = authGuard();
          if (redirect) return redirect;
          return await queryClient.ensureQueryData(
            helpCenterQueries.customerConversations.newTicketPageData(),
          );
        },
      },
      {
        path: 'tickets/:conversationId',
        loader: async ({params}) => {
          const redirect = authGuard();
          if (redirect) return redirect;
          return Promise.all([
            queryClient.ensureQueryData(
              helpCenterQueries.customerConversations.get(
                params.conversationId!,
              ),
            ),
            queryClient.ensureInfiniteQueryData(
              helpdeskQueries.conversations.messages(params.conversationId!),
            ),
          ]);
        },
        lazy: () =>
          import('@app/help-center/tickets-portal/ticket-page/ticket-page'),
      },
      {
        path: 'support/tickets/:ticketId',
        lazy: () => import('@app/student/portal/student-ticket-page'),
      },
      // Unified requests: service requests + tickets + requested documents,
      // each linking back to the screen that owns it.
      {
        path: 'requests',
        lazy: () => import('@app/student/portal/student-requests-page'),
      },
      // The older service-requests-only view. Kept because the unified page
      // links into it for service detail.
      {
        path: 'my-requests',
        lazy: () => import('@app/services/my-requests-page'),
      },
      {
        path: 'my-payments',
        lazy: () => import('@app/services/my-payments-page'),
      },
      {
        path: 'checkout/return',
        lazy: () => import('@app/services/checkout-return-page'),
      },
      // Membership
      {
        path: 'membership',
        lazy: () => import('@app/student/membership/membership-page'),
      },
      // Partner Portal
      {
        // Hours, activities and certificates for somebody volunteering with
        // us — and an invitation to apply for somebody who is not.
        path: 'volunteer',
        lazy: () => import('@app/student/volunteer/volunteer-dashboard-page'),
      },
      {
        // The programme in detail: the journey, every rule the code enforces,
        // a worked example, and what gets an account suspended.
        path: 'partner/guide',
        lazy: () => import('@app/student/partner/partner-guide-page'),
      },
      {
        path: 'partner',
        lazy: () => import('@app/student/partner/partner-dashboard-page'),
      },
    ],
  },
];
